import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import AdminTopBar from '../../components/AdminTopBar';
import { API_BASE_URL } from '../../config';
import DashboardPageShell from '../../components/admin/dashboard/DashboardPageShell';
import DashboardTopBar from '../../components/admin/dashboard/DashboardTopBar';
import DashboardQuickFilterBar from '../../components/admin/dashboard/DashboardQuickFilterBar';
import SnapshotCardsRow from '../../components/admin/dashboard/SnapshotCardsRow';
import SystemHealthStrip from '../../components/admin/dashboard/SystemHealthStrip';
import UnitAmountHealthStrip from '../../components/admin/dashboard/UnitAmountHealthStrip';
import OnboardingSmokeGateCard from '../../components/admin/dashboard/OnboardingSmokeGateCard';
import ActionOpsQueuePanel from '../../components/admin/dashboard/ActionOpsQueuePanel';
import FunnelParityAlertStrip from '../../components/admin/dashboard/FunnelParityAlertStrip';
import DashboardGlobalDrawerHost from '../../components/admin/dashboard/drawers/DashboardGlobalDrawerHost';
import ProductAnalyticsPanel from '../../components/admin/ProductAnalyticsPanel';
import DashboardModuleTabs, { DASHBOARD_WORKSPACE_HIDDEN_PATHS } from '../../components/admin/dashboard/DashboardModuleTabs';
import SegmentAnalysisWorkbench from '../../components/admin/dashboard/SegmentAnalysisWorkbench';
import SegmentSummaryCard from '../../components/admin/dashboard/cards/SegmentSummaryCard';
import FunnelOverviewCard from '../../components/admin/dashboard/cards/FunnelOverviewCard';
import DecisionFeedbackCard from '../../components/admin/dashboard/cards/DecisionFeedbackCard';
import EconomyDeepDiveCard from '../../components/admin/dashboard/cards/EconomyDeepDiveCard';
import ExpertImpactCard from '../../components/admin/dashboard/cards/ExpertImpactCard';
import SessionBehaviorCard from '../../components/admin/dashboard/cards/SessionBehaviorCard';
import MonetizationGuardedSection from '../../components/admin/dashboard/MonetizationGuardedSection';
import PanelHelpBadge from '../../components/admin/PanelHelpBadge';
import DecisionEngineBlock from '../../components/admin/decision-engine/DecisionEngineBlock';
import { olusturAksiyonDurumKapsami } from '../../components/admin/decision-engine/actionStateScope';

const getAdminHeaders = () => {
  const token = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

async function fetchJsonOrNull(url, headers, timeoutMs = 12000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      credentials: 'include',
      headers,
      signal: controller.signal
    });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) return null;
      return null;
    }
    return await res.json();
  } catch (error) {
    // A single slow/broken endpoint should not freeze the whole dashboard.
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

const PROFILE_SEGMENT_OPTIONS = [
  { value: 'yogun-calisan', label: 'Yoğun Çalışan' },
  { value: 'ev-hanimi', label: 'Ev Hanımı' },
  { value: 'ogrenci', label: 'Öğrenci' },
  { value: 'emekli', label: 'Emekli' }
];

function priorityFromSegment({ users = 0, acceptPct = 0, trendPct = 0, confidence = 'LOW' }) {
  const confidenceWeight = confidence === 'HIGH' ? 1 : confidence === 'MEDIUM' ? 0.8 : 0.6;
  const rawScore = ((users * 0.5) + (acceptPct * 1.2) + (Math.max(trendPct, 0) * 4)) * confidenceWeight;
  if (rawScore >= 120) return 'HIGH';
  if (rawScore >= 70) return 'MEDIUM';
  return 'LOW';
}

function confidenceFromBand(confidenceBand = '') {
  const band = String(confidenceBand || '').toLowerCase();
  if (band === 'yuksek') return 'HIGH';
  if (band === 'orta') return 'MEDIUM';
  return 'LOW';
}

function monetizationPotentialFromSegment({ acceptPct = 0, users = 0, confidence = 'LOW', sampleSize = 0 }) {
  if (Number(sampleSize || 0) < 20) return 'LOW';
  const confidenceBoost = confidence === 'HIGH' ? 10 : confidence === 'MEDIUM' ? 0 : -10;
  const score = (acceptPct * 0.7) + (Math.min(users, 200) * 0.2) + confidenceBoost;
  if (score >= 80) return 'HIGH_INTENT';
  if (score >= 50) return 'MEDIUM';
  return 'LOW';
}

export default function AdminDashboard({ initialTab = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const monetizationEnabled = process.env.REACT_APP_ADMIN_MONETIZATION_ENABLED === '1';

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    window: 'default',
    surface: '',
    context: '',
    segment: ''
  });
  const [actions, setActions] = useState({ export: false, manage: false, approve: false });
  const [summary, setSummary] = useState(null);
  const [health, setHealth] = useState(null);
  const [unitAmountHealth, setUnitAmountHealth] = useState(null);
  const [onboardingSmoke, setOnboardingSmoke] = useState(null);
  const [unitAmountGenerating, setUnitAmountGenerating] = useState(false);
  const [decisionFeedback, setDecisionFeedback] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [economySummary, setEconomySummary] = useState(null);
  const [expertSummary, setExpertSummary] = useState(null);
  const [expertImpact, setExpertImpact] = useState(null);
  const [proKpi, setProKpi] = useState(null);
  const [segmentIntelligence, setSegmentIntelligence] = useState(null);
  const [recipePerformance, setRecipePerformance] = useState(null);
  const [unusedRecipes, setUnusedRecipes] = useState(null);
  const [kitchenTypeIntelligence, setKitchenTypeIntelligence] = useState(null);
  const [onboardingSurfaceMetrics, setOnboardingSurfaceMetrics] = useState(null);
  const [activeDrawer, setActiveDrawer] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [profileCardSelections, setProfileCardSelections] = useState(['yogun-calisan', 'ev-hanimi']);

  useEffect(() => {
    if (initialTab === 'analytics') {
      navigate('/admin/product-analytics', { replace: true });
    }
    if (initialTab === 'segment-analysis') {
      navigate('/admin/segment-analysis', { replace: true });
    }
  }, [initialTab, navigate]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const headers = getAdminHeaders();
        const query = new URLSearchParams();
        query.set('window', filters.window);
        if (filters.surface) query.set('surface', filters.surface);
        if (filters.context) query.set('context', filters.context);
        if (filters.segment) query.set('segment', filters.segment);
        const qs = query.toString();

        const [
          accessJson,
          summaryJson,
          healthJson,
          unitAmountHealthJson,
          onboardingSmokeJson,
          segmentJson,
          decisionJson,
          funnelJson,
          performanceJson,
          unusedJson,
          kitchenTypeJson,
          onboardingSurfaceJson,
          economyJson,
          expertSummaryJson,
          expertImpactJson,
          proJson
        ] = await Promise.all([
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/access`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/summary?${qs}`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/health?${qs}`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/unit-amount/health`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/ops-smokes/latest`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/segments/profile-intelligence?${qs}`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/analytics/bbtv/decision-feedback?${qs}`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/funnel?${qs}`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/recipes/performance?${qs}`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/recipes/unused?${qs}`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/kitchen-type-intelligence?${qs}`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/onboarding-surface-metrics?${qs}`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/economy/summary?${qs}`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/experts/summary?${qs}`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/experts/impact?${qs}`, headers),
          fetchJsonOrNull(`${API_BASE_URL}/api/admin/pro-kpi/overview`, headers)
        ]);

        if (!mounted) return;

        setActions({
          export: Boolean(accessJson?.access?.actions?.export),
          manage: Boolean(accessJson?.access?.actions?.manage),
          approve: Boolean(accessJson?.access?.actions?.approve)
        });
        setSummary(summaryJson?.summary || null);
        setHealth(healthJson?.health || null);
        setUnitAmountHealth(unitAmountHealthJson?.unitAmountHealth || null);
        setOnboardingSmoke(onboardingSmokeJson?.smoke || null);
        setSegmentIntelligence(segmentJson || null);
        setDecisionFeedback(decisionJson?.decisionFeedback || null);
        const normalizedFunnel = funnelJson?.funnel?.counts
          ? {
              view: Number(funnelJson.funnel.counts.view || 0),
              select: Number(funnelJson.funnel.counts.select || 0),
              plan: Number(funnelJson.funnel.counts.plan || 0),
              cook: Number(funnelJson.funnel.counts.cook || 0),
              conversion: funnelJson.funnel.rates || {},
              dropOff: {
                viewToSelect: Number(funnelJson.funnel.dropOff?.viewToSelectLossRate || 0),
                selectToPlan: Number(funnelJson.funnel.dropOff?.selectToPlanLossRate || 0),
                planToCook: Number(funnelJson.funnel.dropOff?.planToCookLossRate || 0)
              },
              primaryBreak: funnelJson.funnel.primaryBreak || null,
              trend: funnelJson.funnel.trend || []
            }
          : (funnelJson?.funnel || null);
        setFunnel(normalizedFunnel);
        setRecipePerformance(performanceJson?.performance || null);
        setUnusedRecipes(unusedJson?.unused || null);
        setKitchenTypeIntelligence(kitchenTypeJson?.kitchenTypeIntelligence || null);
        setOnboardingSurfaceMetrics(onboardingSurfaceJson?.metrics || null);
        setEconomySummary(economyJson?.economy || null);
        setExpertSummary(expertSummaryJson?.experts || null);
        setExpertImpact(expertImpactJson?.impact || null);
        setProKpi(proJson?.metrics || null);
      } catch (error) {
        console.error('Admin dashboard load failed', error);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [filters.window, filters.surface, filters.context, filters.segment]);

  const handleGenerateUnitAmountHealth = async () => {
    try {
      setUnitAmountGenerating(true);
      const headers = {
        ...getAdminHeaders(),
        'Content-Type': 'application/json'
      };
      const res = await fetch(`${API_BASE_URL}/api/admin/dashboard/unit-amount/health/generate`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({})
      });
      if (!res.ok) {
        throw new Error(`unit_amount_generate_http_${res.status}`);
      }
      const healthJson = await fetchJsonOrNull(`${API_BASE_URL}/api/admin/dashboard/unit-amount/health`, getAdminHeaders());
      setUnitAmountHealth(healthJson?.unitAmountHealth || null);
    } catch (error) {
      console.error('Unit amount health generate failed', error);
    } finally {
      setUnitAmountGenerating(false);
    }
  };

  const snapshotData = useMemo(() => {
    const activeUsers = Number(summary?.totals?.activeUsers || 0);
    const paidUsers = Number((summary?.totals?.paidUsers ?? summary?.totals?.premiumUsers) || 0);
    const trialUsers = Number(summary?.totals?.trialUsers || 0);
    const freeUsersFromApi = Number(summary?.totals?.freeUsers || 0);
    const freeUsers = Math.max(freeUsersFromApi, Math.max(activeUsers - paidUsers - trialUsers, 0));
    const distribution = Array.isArray(segmentIntelligence?.distribution) ? segmentIntelligence.distribution : [];
    const getSegmentUsers = (key) => {
      const row = distribution.find((item) => String(item?.segment?.key || '').trim() === key);
      return Number(row?.users || 0);
    };

    return {
      user: {
        activeUsers,
        paidUsers,
        trialUsers,
        premiumUsers: paidUsers,
        freeUsers,
        babyUsers: getSegmentUsers('bebekli'),
        allergyUsers: getSegmentUsers('alerjanli')
      },
      cooking: {
        cooked: Number(decisionFeedback?.counts?.cooked || summary?.production?.mealsProduced || 0),
        planned: Number(decisionFeedback?.counts?.planned || 0),
        selected: Number(decisionFeedback?.counts?.selected || 0),
        acceptRate: Number(decisionFeedback?.rates?.acceptRate || 0),
        wowRate: Number(decisionFeedback?.rates?.wowRate || 0)
      },
      economy: {
        monthlyValueTry: Number(economySummary?.summary?.monthlyValueTry || 0),
        yearlyValueTry: Number(economySummary?.summary?.yearlyValueTry || 0),
        aovTry: Number(economySummary?.summary?.aovTry || 0),
        shoppingConversionRate: Number(economySummary?.summary?.shoppingConversionRate || 0)
      },
      expert: {
        activeExperts: Number(proKpi?.retention?.d7ActiveProfessionals || 0),
        totalExperts: Number(expertSummary?.totals?.totalExperts || 0),
        monthlyRevenueTry: Number(expertSummary?.revenue?.monthlyRevenueTry || 0),
        averageImpactScore: Number(expertImpact?.summary?.averageImpactScore || 0)
      }
    };
  }, [summary, decisionFeedback, economySummary, expertSummary, expertImpact, proKpi, segmentIntelligence?.distribution]);

  const segmentCardsByKey = useMemo(() => {
    const requiredDefaults = [
      { key: 'bebekli', name: 'Bebekli', type: 'PROFILE' },
      { key: 'alerjanli', name: 'Alerjanlı', type: 'HEALTH' },
      { key: 'premium', name: 'Premium', type: 'ECONOMY' },
      { key: 'uzmanli', name: 'Uzmanlı', type: 'SYSTEM' },
      { key: 'yogun-calisan', name: 'Yoğun Çalışan', type: 'PROFILE' },
      { key: 'ev-hanimi', name: 'Ev Hanımı', type: 'PROFILE' },
      { key: 'ogrenci', name: 'Öğrenci', type: 'PROFILE' },
      { key: 'emekli', name: 'Emekli', type: 'PROFILE' }
    ];
    const buildEmptySegment = (base) => ({
      key: base.key,
      name: base.name,
      type: base.type,
      scope: 'USER',
      weight: { impact: 0.8, conversion: 0.4, confidence: 0.9 },
      source: 'system',
      confidence: 'LOW',
      users: 0,
      sampleSize: 0,
      orneklemBoyutu: 0,
      orneklemYeterliMi: false,
      uygunluk: 'engelli_dusuk_orneklem',
      tazelikDurumu: 'veri_yok',
      guvenNedenleri: ['veri_yok'],
      kararDili: null,
      aciklanabilirlik: null,
      activityPct: 0,
      acceptPct: 0,
      wowPct: 0,
      recommendedAction: null,
      trendPct: 0,
      priority: 'LOW',
      monetizationPotential: 'LOW',
      hiddenByLowSample: true
    });
    const apiDistribution = Array.isArray(segmentIntelligence?.distribution) ? segmentIntelligence.distribution : [];
    const apiBehavior = Array.isArray(segmentIntelligence?.behavior) ? segmentIntelligence.behavior : [];
    if (apiDistribution.length > 0) {
      const behaviorMap = new Map(apiBehavior.map((row) => [String(row.segmentKey || '').trim(), row]));
      const entries = apiDistribution.map((item) => {
        const segment = item?.segment || {};
        const key = String(segment.key || '').trim();
        const behavior = behaviorMap.get(key) || {};
        const metrics = behavior.metrics || {};
        const confidence = String(
          item?.confidence || behavior?.confidence || confidenceFromBand(item?.guvenBandi || behavior?.guvenBandi) || 'LOW'
        ).toUpperCase();
        const users = Number(item?.users || 0);
        const sampleSize = Number(item?.orneklemBoyutu || behavior?.orneklemBoyutu || item?.sampleSize || users);
        const orneklemYeterliMi = typeof item?.orneklemYeterliMi === 'boolean'
          ? item.orneklemYeterliMi
          : typeof behavior?.orneklemYeterliMi === 'boolean'
            ? behavior.orneklemYeterliMi
            : sampleSize >= 20;
        const acceptPct = Number(metrics?.acceptRate?.value || 0);
        const wowPct = Number(metrics?.wowRate?.value || 0);
        const activityPct = Number(item?.ratioPct || 0);
        const recommendedAction = item?.recommendedAction || behavior?.recommendedAction || null;
        return {
          key,
          name: segment.label || key || 'Segment',
          type: segment.type || 'SYSTEM',
          scope: segment.scope || 'USER',
          weight: segment.weight || { impact: 0.8, conversion: 0.4, confidence: 0.9 },
          source: segment.source || 'system',
          confidence,
          users,
          sampleSize,
          orneklemBoyutu: sampleSize,
          orneklemYeterliMi,
          uygunluk: item?.uygunluk || segment?.uygunluk || behavior?.uygunluk || 'yalniz_icgoru',
          tazelikDurumu: item?.tazelikDurumu || behavior?.tazelikDurumu || 'yaslanan',
          guvenNedenleri: item?.guvenNedenleri || behavior?.guvenNedenleri || [],
          kararDili: item?.kararDili || behavior?.kararDili || null,
          aciklanabilirlik: item?.aciklanabilirlik || behavior?.aciklanabilirlik || null,
          activityPct,
          acceptPct,
          wowPct,
          recommendedAction,
          trendPct: 0,
          priority: priorityFromSegment({
            users,
            acceptPct,
            trendPct: 0,
            confidence
          }),
          monetizationPotential: monetizationPotentialFromSegment({
            acceptPct,
            users,
            confidence,
            sampleSize
          })
        };
      });
      const byKey = entries.reduce((acc, entry) => {
        if (!entry.key) return acc;
        acc[entry.key] = entry;
        return acc;
      }, {});
      for (const base of requiredDefaults) {
        if (byKey[base.key]) continue;
        byKey[base.key] = buildEmptySegment(base);
      }
      return byKey;
    }

    return requiredDefaults.reduce((acc, base) => {
      acc[base.key] = buildEmptySegment(base);
      return acc;
    }, {});
  }, [
    segmentIntelligence?.distribution,
    segmentIntelligence?.behavior
  ]);

  const segmentCards = useMemo(() => {
    const staticKeys = ['bebekli', 'alerjanli', 'premium', 'uzmanli'];
    const staticCards = staticKeys
      .map((key) => segmentCardsByKey[key])
      .filter(Boolean);

    const dynamicCards = profileCardSelections.map((selectedKey, slotIndex) => {
      const fallbackKey = PROFILE_SEGMENT_OPTIONS[slotIndex]?.value || PROFILE_SEGMENT_OPTIONS[0].value;
      const resolvedKey = segmentCardsByKey[selectedKey] ? selectedKey : fallbackKey;
      const segment = segmentCardsByKey[resolvedKey] || segmentCardsByKey[fallbackKey];
      return segment
        ? {
            ...segment,
            slotIndex,
            isProfileSlot: true
          }
        : null;
    }).filter(Boolean);

    return [...staticCards, ...dynamicCards];
  }, [segmentCardsByKey, profileCardSelections]);

  const sessionBehavior = useMemo(
    () => (Array.isArray(segmentIntelligence?.sessionBehavior) ? segmentIntelligence.sessionBehavior : []),
    [segmentIntelligence?.sessionBehavior]
  );

  const funnelParityAlerts = useMemo(() => {
    const alerts = [];
    const shown = Number(decisionFeedback?.counts?.shown || 0);
    const acceptRate = Number(decisionFeedback?.rates?.acceptRate || 0);
    const funnelView = Number(funnel?.view || 0);
    const funnelCook = Number(funnel?.cook || 0);
    const planToCook = Number(funnel?.conversion?.planToCook || 0);
    const decisionSelected = Number(decisionFeedback?.counts?.selected || 0);
    const funnelSelect = Number(funnel?.select || 0);

    if (shown === 0 && acceptRate > 0) {
      alerts.push('Gösterim 0 iken kabul oranı var; decision event akışı kontrol edilmeli.');
    }
    if (planToCook > 100) {
      alerts.push('Plan -> Pişirme %100 üzerinde; funnel event sıralaması kontrol edilmeli.');
    }
    if (funnelCook > funnelView && funnelView > 0) {
      alerts.push('Pişirme sayısı görüntüleme sayısından yüksek; veri kontratı uyumsuz olabilir.');
    }
    if (decisionSelected > 0 && funnelSelect > 0) {
      const delta = Math.abs(decisionSelected - funnelSelect);
      const maxBase = Math.max(decisionSelected, funnelSelect, 1);
      const diffPct = (delta / maxBase) * 100;
      if (diffPct >= 20) {
        alerts.push('Decision seçimi ile funnel seçimi arasında %20+ fark var; parity kontrolü gerekli.');
      }
    }

    return alerts;
  }, [decisionFeedback, funnel]);

  const dashboardHeroDecision = useMemo(() => {
    const activeUsers = Number(snapshotData?.user?.activeUsers || 0);
    const paidUsers = Number((snapshotData?.user?.paidUsers ?? snapshotData?.user?.premiumUsers) || 0);
    const trialUsers = Number(snapshotData?.user?.trialUsers || 0);
    const freeUsers = Number(snapshotData?.user?.freeUsers || 0);
    const paidRate = activeUsers > 0 ? (paidUsers / activeUsers) * 100 : 0;
    const acceptRatePct = Number(decisionFeedback?.rates?.acceptRate || 0) * 100;
    const conversionPct = Number(economySummary?.summary?.shoppingConversionRate || 0);

    const anomalies = [];
    if (activeUsers === 0 && (paidUsers > 0 || trialUsers > 0 || freeUsers > 0)) {
      anomalies.push({
        title: 'Aktif kullanici 0 iken alt kirilimlarda deger var.',
        meaning: 'Toplam ve alt metrikler ayni olcumu yansitmiyor olabilir.',
        risk: 'Yanlis aksiyon onceligi uretebilir.',
        recommendation: 'Kullanici kaynak event esitligini kontrol et.'
      });
    }
    if (paidUsers > activeUsers) {
      anomalies.push({
        title: 'Ucretli kullanici sayisi toplam aktif kullanicidan yuksek gorunuyor.',
        meaning: 'Ucretli ve aktif tanimlari farkli pencereden geliyor olabilir.',
        risk: 'Gelir odakli kampanya kararlarini bozar.',
        recommendation: 'Window ve segment eslesmesini dogrula.'
      });
    }
    if (Number(funnel?.cook || 0) > Number(funnel?.view || 0) && Number(funnel?.view || 0) > 0) {
      anomalies.push({
        title: 'Pisirme sayisi goruntulemeden yuksek gorunuyor.',
        meaning: 'Funnel event sirasi tutarsiz olabilir.',
        risk: 'Dusus noktasi analizi yaniltici olur.',
        recommendation: 'Funnel event kontratini parity loglariyla dogrula.'
      });
    }

    let primaryActionTitle = 'Segment bazli teklif akisinda dusuk donusum noktalarini one al.';
    let primaryReason = 'Karar motoru, potansiyel artisin en hizli test edilebilecegi alani isaret ediyor.';
    let expectedImpact = 'Kabul ve ucretli donusum dengesinde olculebilir iyilesme.';
    let score = 0.48;
    let priority = 'LOW';

    if (activeUsers > 0 && paidRate < 25) {
      primaryActionTitle = 'Ucretli donusum dusuk, bu kitlede deneme/teklif akislarini one cikar.';
      primaryReason = `Ucretli oran %${paidRate.toFixed(1)} seviyesinde; daha hizli kazanimi burada aliriz.`;
      expectedImpact = 'Ucretliye gecis oraninda +1 ila +3 puan artis hedeflenir.';
      score = 0.84;
      priority = 'HIGH';
    } else if (acceptRatePct < 20) {
      primaryActionTitle = 'Kabul orani dusuk, oneriyi sadelestirip ilk adimi kisalt.';
      primaryReason = `Kabul orani %${acceptRatePct.toFixed(1)} seviyesinde; oneriden aksiyona gecis surtunmeli.`;
      expectedImpact = 'Kabul oraninda +2 ila +5 puan artis hedeflenir.';
      score = 0.72;
      priority = 'MEDIUM';
    } else if (conversionPct < 3 && activeUsers > 0) {
      primaryActionTitle = 'Ekonomi donusumu zayif, yuksek niyetli segmentte teklif zamanlamasini test et.';
      primaryReason = `Alisveris donusumu %${conversionPct.toFixed(1)} seviyesinde; gelir etkisi sinirli kaliyor.`;
      expectedImpact = 'Sepet donusumunde +0.5 ila +1.5 puan artis hedeflenir.';
      score = 0.67;
      priority = 'MEDIUM';
    }

    const confidence = activeUsers >= 100 ? 'YÜKSEK' : activeUsers >= 30 ? 'ORTA' : 'DÜŞÜK';
    const actionLock = anomalies.length > 0 || activeUsers < 10;
    const actionLockReason = anomalies.length > 0
      ? 'Kritik veri uyarisi varken aksiyonu canliya almadan once kontrat kontrolu yap.'
      : 'Orneklem cok dusuk; once izleme modunda sinyal topla.';

    return {
      keyTakeaway: 'Karar once gorunmeli, detay ikinci adim olmali.',
      narrative: 'Kontrol Merkezi acildigi anda birincil aksiyon ve risk bandi gorunur olmalidir.',
      actions: [
        {
          actionKey: 'dashboard-primary-action',
          title: primaryActionTitle,
          reason: primaryReason,
          expectedImpact,
          score,
          context: {
            orneklem: activeUsers,
            segment: filters.segment || 'tum',
            pencere: filters.window || 'default'
          }
        }
      ],
      anomalies,
      priority,
      confidence,
      sampleSize: activeUsers,
      actionLock,
      actionLockReason,
      expectedOutcome: expectedImpact
    };
  }, [
    snapshotData?.user?.activeUsers,
    snapshotData?.user?.paidUsers,
    snapshotData?.user?.trialUsers,
    snapshotData?.user?.premiumUsers,
    snapshotData?.user?.freeUsers,
    decisionFeedback?.rates?.acceptRate,
    economySummary?.summary?.shoppingConversionRate,
    funnel?.cook,
    funnel?.view,
    filters.segment,
    filters.window
  ]);

  const dashboardHeroActionScope = useMemo(() => (
    olusturAksiyonDurumKapsami('dashboard-hero', {
      filters,
      payload: { key: 'global-hero' }
    })
  ), [filters]);

  const handleProfileCardSelect = (slotIndex, nextKey) => {
    setProfileCardSelections((prev) => {
      const next = [...prev];
      const duplicateIndex = next.findIndex((key, index) => index !== slotIndex && key === nextKey);
      if (duplicateIndex >= 0) {
        next[duplicateIndex] = next[slotIndex];
      }
      next[slotIndex] = nextKey;
      return next;
    });
  };

  const refreshedAtLabel = useMemo(() => {
    const value = summary?.generatedAt || health?.generatedAt || null;
    if (!value) return 'Bilinmiyor';
    try {
      return new Date(value).toLocaleString('tr-TR');
    } catch (_) {
      return 'Bilinmiyor';
    }
  }, [summary?.generatedAt, health?.generatedAt]);

  const handleOpenDrawer = (drawerKey, payload = null) => {
    const defaultPayloadByDrawer = {
      cooking: {
        funnel,
        decisionFeedback,
        recipePerformance,
        unusedRecipes,
        kitchenTypeIntelligence
      },
      funnel: {
        funnel,
        decisionFeedback,
        recipePerformance,
        unusedRecipes,
        kitchenTypeIntelligence
      },
      economy: {
        economy: economySummary
      },
      expert: {
        experts: expertSummary,
        impact: expertImpact
      },
      session: {
        sessionBehavior
      },
      'segment-builder': {
        source: 'segment-insight',
        permissions: {
          canAdvancedMode: Boolean(actions.manage),
          canPublish: Boolean(actions.approve)
        }
      }
    };

    setActiveDrawer(drawerKey);
    const defaultPayload = defaultPayloadByDrawer[drawerKey] || null;
    const mergedPayload = defaultPayload && payload
      ? { ...defaultPayload, ...payload }
      : (payload || defaultPayload || null);

    setSelectedEntity({
      drawerKey,
      filters,
      snapshot: snapshotData[drawerKey] || null,
      payload: mergedPayload
    });
  };

  const handleCloseDrawer = () => {
    setActiveDrawer('');
    setSelectedEntity(null);
  };

  const handleExport = () => {
    const payload = {
      filters,
      snapshotData,
      health
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kontrol-merkezi-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex h-screen w-full">
      <AdminSidebar
        mobileOpen={mobileSidebarOpen}
        onCloseMobile={() => setMobileSidebarOpen(false)}
        hiddenPaths={DASHBOARD_WORKSPACE_HIDDEN_PATHS}
      />

      <main className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50 dark:bg-slate-900 relative md:ml-[var(--admin-sidebar-width,18rem)] transition-[margin] duration-300">
        <AdminTopBar title="Kontrol Merkezi" onToggleMobile={() => setMobileSidebarOpen((open) => !open)} />

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mx-auto max-w-[1440px]">
            <DashboardPageShell>
              <DashboardModuleTabs />

              <DashboardTopBar
                filters={filters}
                onFilterChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
                canExport={actions.export}
                onExport={handleExport}
                refreshedAtLabel={refreshedAtLabel}
              />
              <DashboardQuickFilterBar
                selectedSegment={filters.segment}
                onSelectSegment={(segment) => setFilters((prev) => ({ ...prev, segment }))}
              />

              {location.pathname === '/admin/product-analytics' ? (
                <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#101922] p-4 shadow-sm">
                  <ProductAnalyticsPanel embedded={true} />
                </section>
              ) : location.pathname === '/admin/segment-analysis' ? (
                <>
                  <SegmentAnalysisWorkbench
                    segmentCards={segmentCards}
                    snapshotData={snapshotData}
                    decisionFeedback={decisionFeedback}
                    economySummary={economySummary}
                    kitchenTypeIntelligence={kitchenTypeIntelligence}
                    filters={filters}
                    onOpenDrawer={handleOpenDrawer}
                  />
                  {loading ? (
                    <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#101922] p-4 text-sm text-slate-500 dark:text-slate-400">
                      Segment Analizi verileri yükleniyor...
                    </section>
                  ) : null}
                </>
              ) : (
                <>
                  <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-[#101922]">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Hero Karar Bandi</p>
                        <h3 className="text-base font-black text-slate-900 dark:text-white">Aksiyon once, detay sonra</h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleOpenDrawer('user')}
                        className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                      >
                        Detay
                      </button>
                    </div>

                    {dashboardHeroDecision.anomalies.length > 0 ? (
                      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
                        <p className="font-semibold">Kritik Uyari</p>
                        <p className="text-xs">{dashboardHeroDecision.anomalies[0].title}</p>
                      </div>
                    ) : null}

                    <DecisionEngineBlock
                      narrative={dashboardHeroDecision.narrative}
                      keyTakeaway={dashboardHeroDecision.keyTakeaway}
                      actions={dashboardHeroDecision.actions}
                      anomalies={dashboardHeroDecision.anomalies}
                      priority={dashboardHeroDecision.priority}
                      confidence={dashboardHeroDecision.confidence}
                      sampleSize={dashboardHeroDecision.sampleSize}
                      actionLock={dashboardHeroDecision.actionLock}
                      actionLockReason={dashboardHeroDecision.actionLockReason}
                      expectedOutcome={dashboardHeroDecision.expectedOutcome}
                      actionStateScope={dashboardHeroActionScope}
                    />
                  </section>

                  <SnapshotCardsRow data={snapshotData} onOpenDrawer={handleOpenDrawer} />
                  <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#101922]">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <h3 className="text-sm font-black text-slate-900 dark:text-white">Onboarding Surface KPI</h3>
                      <span className="text-[11px] text-slate-500 dark:text-slate-400">suggested -> seen -> acted / ignored</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                      <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">Sent</p>
                        <p className="text-lg font-black text-slate-900 dark:text-white">{Number(onboardingSurfaceMetrics?.counts?.suggested || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">Seen</p>
                        <p className="text-lg font-black text-slate-900 dark:text-white">{Number(onboardingSurfaceMetrics?.counts?.seen || 0).toLocaleString('tr-TR')}</p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">%{Number(onboardingSurfaceMetrics?.rates?.seenRate || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">Acted</p>
                        <p className="text-lg font-black text-slate-900 dark:text-white">{Number(onboardingSurfaceMetrics?.counts?.acted || 0).toLocaleString('tr-TR')}</p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">%{Number(onboardingSurfaceMetrics?.rates?.actedRate || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">Ignored</p>
                        <p className="text-lg font-black text-slate-900 dark:text-white">{Number(onboardingSurfaceMetrics?.counts?.ignored || 0).toLocaleString('tr-TR')}</p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">%{Number(onboardingSurfaceMetrics?.rates?.ignoredRate || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">Acted Value (TL)</p>
                        <p className="text-lg font-black text-emerald-600 dark:text-emerald-400">{Number(onboardingSurfaceMetrics?.value?.actedValueTry || 0).toLocaleString('tr-TR')}</p>
                      </div>
                      <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">Acted Days Saved</p>
                        <p className="text-lg font-black text-indigo-600 dark:text-indigo-400">{Number(onboardingSurfaceMetrics?.value?.actedDaysSaved || 0).toLocaleString('tr-TR')}</p>
                      </div>
                    </div>
                  </section>
                  <ActionOpsQueuePanel />
                  <SystemHealthStrip health={health} />
                  <OnboardingSmokeGateCard data={onboardingSmoke} />
                  <UnitAmountHealthStrip
                    data={unitAmountHealth}
                    onGenerate={handleGenerateUnitAmountHealth}
                    generating={unitAmountGenerating}
                  />

                  <section className="space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-black text-slate-900 dark:text-white">Segment İçgörüsü</h3>
                        <PanelHelpBadge
                          title="Segment İçgörüsü"
                          description="Sabit 6 segmentte aktiflik, kabul ve wow sinyallerini karşılaştırmalı gösterir."
                          interpretation="Güven düşükse sinyal trendini takip et, tek metrikle karar verme."
                          metricExample="Aynı dönemde premium artıp kabul düşüyorsa segment özel öneri kalitesi gözden geçirilir."
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500 dark:text-slate-400">4 sabit + 2 dropdown segment görünümü</span>
                        <button
                          type="button"
                          onClick={() => navigate('/admin/segment-analysis')}
                          className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                        >
                          Segment Analizine Git
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenDrawer('segment-builder', { source: 'segment-insight' })}
                          className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                        >
                          Daha detaylı analiz yap
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {segmentCards.map((segment) => (
                        <SegmentSummaryCard
                          key={segment.isProfileSlot ? `profile-slot-${segment.slotIndex}` : segment.key}
                          segment={segment}
                          onOpenDrawer={handleOpenDrawer}
                          segmentOptions={segment.isProfileSlot ? PROFILE_SEGMENT_OPTIONS : null}
                          onSelectSegmentKey={segment.isProfileSlot ? (nextKey) => handleProfileCardSelect(segment.slotIndex, nextKey) : null}
                        />
                      ))}
                    </div>
                  </section>

                  <section className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                    <FunnelOverviewCard funnel={funnel} onOpenDrawer={handleOpenDrawer} />
                    <DecisionFeedbackCard decisionFeedback={decisionFeedback} onOpenDrawer={handleOpenDrawer} />
                    <SessionBehaviorCard sessionBehavior={sessionBehavior} onOpenDrawer={handleOpenDrawer} />
                  </section>

                  <FunnelParityAlertStrip alerts={funnelParityAlerts} />

                  <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <EconomyDeepDiveCard economy={economySummary} onOpenDrawer={handleOpenDrawer} />
                    <ExpertImpactCard experts={expertSummary} impact={expertImpact} onOpenDrawer={handleOpenDrawer} />
                  </section>

                  <MonetizationGuardedSection enabled={monetizationEnabled} />

                  {loading ? (
                    <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#101922] p-4 text-sm text-slate-500 dark:text-slate-400">
                      Kontrol Merkezi verileri yükleniyor...
                    </section>
                  ) : null}
                </>
              )}
            </DashboardPageShell>
          </div>
        </div>
      </main>

      <DashboardGlobalDrawerHost
        activeDrawer={activeDrawer}
        selectedEntity={selectedEntity}
        onClose={handleCloseDrawer}
      />
    </div>
  );
}
